import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, RefreshCw, X } from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import NewtonsCradleLoader from '../../components/common/NewtonsCradleLoader';

const THEME = getThemeColor('primary');

const TRADERS_INFO_TEXT =
  'This section displays your most recent referred clients (ClientCode), their verification status, country of origin, and registration date. You can find more information in the “Reports” section under “Trader List”.';

const DashboardTraders = ({ traders = [], loading, onRefresh }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const infoRef = useRef(null);

  useEffect(() => {
    if (!infoOpen) return undefined;
    const onPointerDown = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setInfoOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen]);

  return (
    <section className="dash-side-card flex flex-col min-h-[280px]">
      <div className="dash-side-card__head">
        <h2 className="dash-side-card__title">Traders</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="w-7 h-7 rounded-full border border-gray-200 text-gray-500 flex items-center justify-center hover:bg-gray-50 transition disabled:opacity-50"
            aria-label="Refresh traders"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div
            className="relative"
            ref={infoRef}
            onMouseEnter={() => setInfoOpen(true)}
            onMouseLeave={() => setInfoOpen(false)}
          >
            <button
              type="button"
              onClick={() => setInfoOpen((v) => !v)}
              className="w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] font-bold leading-none flex items-center justify-center hover:bg-black transition"
              aria-label="Traders information"
              aria-expanded={infoOpen}
            >
              i
            </button>
            {infoOpen && (
              <div
                role="tooltip"
                className="absolute right-0 top-full z-20 pt-2.5 w-[min(300px,calc(100vw-3rem))]"
              >
                <div className="relative rounded-md border border-gray-200 bg-white px-3 py-2.5 text-[11px] leading-relaxed text-gray-700 shadow-md">
                  <span
                    className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 border-l border-t border-gray-200 bg-white"
                    aria-hidden
                  />
                  {TRADERS_INFO_TEXT}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto max-h-[320px]">
        {loading && traders.length === 0 ? (
          <div className="flex justify-center py-12">
            <NewtonsCradleLoader size="24px" />
          </div>
        ) : traders.length === 0 ? (
          <div className="dash-empty">No traders found</div>
        ) : (
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-xs font-semibold text-gray-700">
                <th className="px-4 py-2.5 font-semibold">ClientCode</th>
                <th className="px-3 py-2.5 font-semibold">Country</th>
                <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Registration Date</th>
              </tr>
            </thead>
            <tbody>
              {traders.map((row) => (
                <tr key={row.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Link
                        to={
                          row.clientCode && row.clientCode !== '—'
                            ? `/clients?search=${encodeURIComponent(row.clientCode)}`
                            : '/clients'
                        }
                        className="text-sm font-semibold truncate hover:underline"
                        style={{ color: THEME }}
                        title="Open client listing"
                      >
                        {row.clientCode}
                      </Link>
                      {row.verified ? (
                        <span
                          className="shrink-0 w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center"
                          title="Verified"
                        >
                          <Check className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                      ) : (
                        <span
                          className="shrink-0 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center"
                          title="Not verified"
                        >
                          <X className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-700">{row.country}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                    {row.signUpDate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default DashboardTraders;
